import {
  DEFAULT_PAGE_SIZE,
  LAST_DAYS_SORT_BY,
  MEETING_ADOPTION_TABLE_COLUMNS,
  MS_MEETING_ADOPTION_SORT_BY,
} from '@common/constants';
import { ExtendedOption, IOption, OrderBy } from '@common/types';
import { OverlayPage, TableWrapper } from '@components/partials';
import { FilterData } from '@components/partials/TableWrapper/parts/Filter';
import { Dropdown } from '@cloudclevr/components';
import { useMeetingAdoptionPreviewQuery } from '@hooks/adoption';
import { PeopleSortBy } from '@hooks/people/types';
import { useIsDesktop, useIsMobile } from '@hooks/utils';
import { usePagination } from '@hooks/utils/pagination';
import { MeetingAdoptionSnapshot } from '@pages/Dashboard/tiles/MeetingAdoptionSnapshot';
import { formatDate, useTableData } from '@utils/index';
import React, { useEffect, useState } from 'react';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import styles from './styles.module.css';
import { createCellValue } from './utils';
import { DailyMeetingAttendanceTrend } from '@pages/Dashboard/tiles/DailyMeetingAttendanceTrend';
import { useNavSourceRedirect } from '@hooks/utils/dashboard';
import { useLocationsQuery } from '@hooks/locations';
import { useGroupsFilterQuery } from '@hooks/groups';
import { GroupsResponse } from '@hooks/groups/types';
import { Location } from '@hooks/locations/types';
import { RecommendationCard } from '@pages/Dashboard/tiles/helpers/RecommendationCard';
import useDownloadCsvSafe from '@hooks/utils/export/useDownloadCsvSafe';
import { API_ENDPOINTS } from '@api/ApiEndpoints';

const pageSize = DEFAULT_PAGE_SIZE;
const floatingSortDropdownData = LAST_DAYS_SORT_BY;

const MeetingAdoptionPage: React.FC = () => {
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();
  const [search, setSearch] = useState('');
  const { pageNumber, setPageNumber } = usePagination();
  const [days, setDays] = useState('30');
  const [daysSelectedLabel, setDaysSelectedLabel] = useState(
    floatingSortDropdownData[1].label || ''
  );
  const [daysSelectedIndex, setDaysSelectedIndex] = useState(1);
  const [isDaysDropdownOpen, setIsDaysDropdownOpen] = useState(false);
  const redirectPath = useNavSourceRedirect('/insights-and-analytics/adoption');

  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [queryParams, setQueryParams] = useQueryParams({
    search: withDefault(StringParam, ''),
    sortBy: withDefault(StringParam, ''),
    order: withDefault(StringParam, ''),
    filter: withDefault(StringParam, 'licensedonly'),
  });

  const { data: locationsData, isFetched: locationDataFetched } =
    useLocationsQuery();
  const { data: groupsData, isFetched: groupsDataFetched } =
    useGroupsFilterQuery();

  const FILTERS = [
    'hasntsetupms',
    'hasntjoinedms',
    'hasntsetuprc',
    'hasntjoinedrc',
  ];

  const LICENSE_FILTER_VALUE = 'licensedonly';
  const licensedOnly = queryParams.filter?.split(',').includes(LICENSE_FILTER_VALUE) ?? true;

  const allLocationIds: string[] = [];
  const groupsIDs: string[] = [];
  const locationFilerOptions: IOption[] = [];
  const groupFilerOptions: ExtendedOption[] = [];

  const inactivePeopleFilter: FilterData = {
    label: 'Inactive people',
    name: 'filter',
    options: [
      { label: 'Didn’t set up an MS Teams meeting', value: 'hasntsetupms' },
      {
        label: 'Didn’t attend an MS Teams meeting',
        value: 'hasntjoinedms',
      },
      {
        label: 'Didn’t set up a RingCentral meeting',
        value: 'hasntsetuprc',
      },
      {
        label: 'Didn’t attend a RingCentral meeting',
        value: 'hasntjoinedrc',
      },
    ],
  };

  const licenseFilter: FilterData = {
    label: 'User accounts with assigned licences',
    name: 'licensedOnly',
    description: 'Untick to display for all user accounts.',
    options: [
      {
        label: 'User accounts with licences assigned',
        value: LICENSE_FILTER_VALUE,
        testId: 'meeting-licensed-users-checkbox',
      },
    ],
  };

  if (locationDataFetched && groupsDataFetched) {
    if (locationsData) {
      locationsData?.forEach((location: Location) => {
        locationFilerOptions.push({ label: location.name, value: location.id });
        allLocationIds.push(location.id);
      });
    }

    if (groupsData) {
      groupsData?.forEach((group: GroupsResponse) => {
        groupFilerOptions.push({
          name: group.name,
          value: group.id,
          parentId: group.parentId,
          subGroups: group.subGroups,
          id: group.id,
          label: group.name,
        });
      });
    }
  }

  const locationFilter: FilterData | null =
    locationDataFetched && groupsDataFetched && locationsData
      ? {
          label: 'Location',
          name: 'location',
          singleSelect: false,
          options: locationFilerOptions,
        }
      : null;

  const groupsFilter: FilterData | null =
    locationDataFetched && groupsDataFetched && groupsData
      ? {
          label: 'Groups',
          name: 'groups',
          singleSelect: false,
          options: groupFilerOptions,
        }
      : null;

  const transformedFilterData: FilterData[] = [
    inactivePeopleFilter,
    licenseFilter,
    ...(locationFilter ? [locationFilter] : []),
    ...(groupsFilter ? [groupsFilter] : []),
  ];

  useEffect(() => {
    locationsData?.forEach((location: Location) => {
      locationFilerOptions.push({ label: location.name, value: location.id });
      allLocationIds.push(location.id);
    });
    setLocationIds(allLocationIds);
  }, [locationsData]);

  useEffect(() => {
    const nested = (element: any) => {
      element.subGroups.forEach((subElement: any) => {
        groupsIDs.push(subElement.id);
        if (subElement.subGroups && subElement.subGroups.length > 0) {
          nested(subElement);
        }
      });
    };

    groupsData?.forEach((element: any) => {
      groupsIDs.push(element.id);
      if (element.subGroups && element.subGroups.length > 0) {
        nested(element);
      }
    });

    setGroupIds(groupsIDs);
  }, [groupsData]);

  // table
  const query = useMeetingAdoptionPreviewQuery({
    days,
    pageSize,
    pageNumber,
    searchTerm: search,
    sortBy: queryParams.sortBy as PeopleSortBy,
    order: queryParams.order as OrderBy,
    locationList: queryParams.filter
      ? queryParams.filter
          .split(',')
          .filter((filter) => locationIds.includes(filter))
      : [],
    groupList: queryParams.filter
      ? queryParams.filter
          .split(',')
          .filter((filter) => groupIds.includes(filter))
      : [],
    meetingAdoptionFilter: queryParams.filter
      ? queryParams.filter
          .split(',')
          .filter((filter) => FILTERS.includes(filter))
      : [],
    licensedOnly,
  });
  const getTableData = useTableData(
    query,
    MEETING_ADOPTION_TABLE_COLUMNS,
    createCellValue
  );

  const handleIsDaysDropdownOpen = () => {
    setIsDaysDropdownOpen(!isDaysDropdownOpen);
  };

  const handleDropdownDaysOnChange = (value: string[], index: number) => {
    setDays(value[0]);
    setDaysSelectedIndex(index);
    setDaysSelectedLabel(floatingSortDropdownData[index].label);
    query.refetch();
  };

  const { handleDownloadClick } = useDownloadCsvSafe({
    endpoint: `${API_ENDPOINTS.MEETING_ADOPTION}/export`,
    params: {
      days,
      pageSize,
      pageNumber,
      searchTerm: search,
      sortBy: queryParams.sortBy as PeopleSortBy,
      order: queryParams.order as OrderBy,
      locationList: queryParams.filter
        ? queryParams.filter
            .split(',')
            .filter((filter) => locationIds.includes(filter))
        : [],
      groupList: queryParams.filter
        ? queryParams.filter
            .split(',')
            .filter((filter) => groupIds.includes(filter))
        : [],
      meetingAdoptionFilter: queryParams.filter
        ? queryParams.filter
            .split(',')
            .filter((filter) => FILTERS.includes(filter))
        : [],
      LicensedOnly: licensedOnly,
    },
    filename: `Clevr360_Meeting_adoption_${formatDate(
      new Date().toString()
    )}.csv`,
  });


  return (
    <OverlayPage
      isFooterVisible={false}
      contentClassNames={''}
      path={redirectPath}
      headerTitle={`Meeting adoption`}
    >
      <>
        {!isMobile && (
          <div className="absolute left-[40px] top-[113px]">
            <Dropdown testId="meeting-adoption-days-selector">
              <Dropdown.TextHeader
                classNames="!bg-interfaceColor-5 !border !border-interfaceColor-100 rounded-full"
                label={daysSelectedLabel}
                handleOpen={handleIsDaysDropdownOpen}
              ></Dropdown.TextHeader>
              <Dropdown.List
                className="!z-[999] text-16"
                open={isDaysDropdownOpen}
                align="left"
              >
                {floatingSortDropdownData.map((item, index) => {
                  return (
                    <Dropdown.DefaultItem
                      key={index}
                      item={item}
                      currentIndex={index}
                      selectedIndex={daysSelectedIndex}
                      onChange={() =>
                        handleDropdownDaysOnChange(item.value, index)
                      }
                    ></Dropdown.DefaultItem>
                  );
                })}
              </Dropdown.List>
            </Dropdown>
          </div>
        )}
        <div className={styles.pageGrid}>
          {isDesktop && (
            <>
              <MeetingAdoptionSnapshot
                classNames="col-span-6 md:col-span-2 xl:col-span-1"
                params={JSON.stringify({ days, type: 'snapshot', licensedOnly })}
              />
              <DailyMeetingAttendanceTrend
                classNames="col-span-6 md:col-span-2 xl:col-span-2"
                params={JSON.stringify({ days })}
              />
              <RecommendationCard insightDescription={'meeting adoption'} />
            </>
          )}
        </div>
        <div className="mt-2">
          <TableWrapper
            testId='meeting-adoption-table'
            search={search}
            setSearch={setSearch}
            searchPlaceholder={'Search by name'}
            columns={MEETING_ADOPTION_TABLE_COLUMNS}
            data={getTableData()}
            sortData={MS_MEETING_ADOPTION_SORT_BY}
            filterData={transformedFilterData}
            searchKey="search"
            query={query}
            refetchQuery={query.refetch}
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            setPageNumber={setPageNumber}
            isCollapsable={true}
            sliceColumns={1}
            floatingFilterButton={true}
            hasFilterBanner={false}
            searchCountStatsLabel="people"
            floatingFilterButtonLeft={!isMobile ? '190px' : ''}
            filterHeaderText="Filter meeting adoption"
            isDownloadAvailable={true}
            handleDownload={handleDownloadClick}
            noResultTitle={
              query.isError
                ? undefined
                : 'No user accounts match the selected filters.'
            }
            noResultSubtitle={
              query.isError
                ? undefined
                : 'Try adjusting your filters or clear them to see all user accounts.'
            }
          />
        </div>
      </>
    </OverlayPage>
  );
};

export default MeetingAdoptionPage;
